import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthContext, useNotificationContext } from "../../context";
import Sidebar from "./Sidebar";
import SearchModal from "../Modal/SearchModal";
import { FaBars, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";

const Navbar = () => {
  const { user, logoutHandler } = useAuthContext();
  const { chatUnreadCount } = useNotificationContext();
  const navigate = useNavigate();
  const location = useLocation();

  const hideSearchInbox = ["/search", "/dashboard"].includes(location.pathname);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleRef = useRef();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setDropdownOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Log out?",
      text: "You will be signed out of your account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, log me out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-xl shadow-xl p-4 max-w-sm",
        confirmButton:
          "bg-red-500 text-white px-4 py-2 rounded-full hover:bg-red-600 focus:outline-none",
        cancelButton:
          "bg-gray-100 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-200 focus:outline-none",
        title: "text-lg font-semibold text-gray-800",
        htmlContainer: "text-sm text-gray-600",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        logoutHandler();
        navigate("/login", { replace: true });
        setDropdownOpen(false);
      }
    });
  };

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      {/* Mobile drawer */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSearch={() => {
          setShowSearchModal(true);
          setSidebarOpen(false);
        }}
      />

      <nav className="fixed top-0 w-full z-50 bg-white bg-opacity-95 backdrop-blur-sm shadow py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left - Logo */}
          <div
            onClick={() => {
              scrollToTop();
              navigate("/");
            }}
            className="cursor-pointer h-12 w-40 flex items-center"
          >
            <img
              src="/image/splash-transparent.png"
              alt="SomeOne4u Logo"
              className="h-full w-auto object-contain"
            />
          </div>

          {/* Center - Nav Links (desktop only) */}
          <div className="hidden md:flex flex-1 justify-center space-x-8">
            {!hideSearchInbox && (
              <button
                onClick={() => setShowSearchModal(true)}
                className="text-gray-700 hover:text-red-500 font-medium"
              >
                Search
              </button>
            )}

            {user && location.pathname !== "/dashboard" && (
              <button
                onClick={() => {
                  navigate("/dashboard");
                  scrollToTop();
                }}
                className="text-gray-700 hover:text-red-500 font-medium"
              >
                Dashboard
              </button>
            )}

            {user && !hideSearchInbox && (
              <Link
                to="/inbox"
                onClick={scrollToTop}
                className="relative text-gray-700 hover:text-red-500 font-medium"
              >
                Inbox
                {chatUnreadCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    !
                  </span>
                )}
              </Link>
            )}

            {user && !hideSearchInbox && !user.membership_status && (
              <Link
                to="/membership"
                onClick={scrollToTop}
                className="text-gray-700 hover:text-red-500 font-medium"
              >
                Upgrade
              </Link>
            )}
          </div>

          {/* Right - Auth / Profile */}
          <div className="flex items-center">
            {user ? (
              <div className="relative flex items-center space-x-2">
                {/* Profile Toggle Button */}
                <div
                  ref={toggleRef}
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => setDropdownOpen((prev) => !prev)} // Toggle dropdown open/close
                >
                  <span className="text-gray-700">Hi, {user.firstName}</span>
                  <img
                    src={user.profile_image || "/image/operator.png"}
                    alt="Profile"
                    className="w-8 h-8 rounded-full"
                  />
                  {!isMobile && (
                    <span className="text-gray-700 select-none">▼</span>
                  )}
                </div>

                {/* Dropdown Menu */}
                {!isMobile && dropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute right-0 top-full mt-2 w-40 bg-white border rounded shadow-lg z-50"
                    onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing when clicking inside
                  >
                    <Link
                      to="/profile"
                      onClick={() => {
                        scrollToTop();
                        setDropdownOpen(false); // Close dropdown when navigating to profile
                      }}
                      className="block px-4 py-2 text-gray-700 hover:text-red-400 hover:bg-gray-200"
                    >
                      My Profile
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent click from closing the dropdown
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:text-red-400 hover:bg-gray-200"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              !isMobile && (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/signup"
                    onClick={scrollToTop}
                    className="text-gray-700 hover:text-red-500 font-medium"
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/login"
                    onClick={scrollToTop}
                    className="text-gray-700 hover:text-red-500 font-medium"
                  >
                    Log In
                  </Link>
                </div>
              )
            )}
            {/* Hamburger */}
            <button
              className="md:hidden text-gray-700 focus:outline-none ml-4"
              onClick={() => setSidebarOpen((o) => !o)}
            >
              {sidebarOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div className="h-16" />

      {/* Search Modal */}
      {showSearchModal && (
        <SearchModal
          initialAge={[18, 45]}
          initialGender=""
          initialLookingFor=""
          initialCity=""
          initialState=""
          onSearchUpdate={(q) => {
            localStorage.setItem("searchQuery", JSON.stringify(q));
            setShowSearchModal(false);
            navigate("/search", { state: q });
            scrollToTop();
          }}
          onClose={() => setShowSearchModal(false)}
        />
      )}
    </>
  );
};

export default Navbar;
